import type { LtiChildRecordsExistError } from '@efdigital/ll-cdk-lti-registration-api-types';

const CHILD_RECORDS_EXIST_CODE = 'CHILD_RECORDS_EXIST' as const;

export function isLtiChildRecordsExistError(
  body: unknown
): body is LtiChildRecordsExistError {
  if (typeof body !== 'object' || body === null) {
    return false;
  }

  const candidate = body as LtiChildRecordsExistError;
  return (
    candidate.code === CHILD_RECORDS_EXIST_CODE &&
    typeof candidate.childType === 'string' &&
    typeof candidate.count === 'number'
  );
}

export function formatLtiChildRecordsExistMessage(
  error: LtiChildRecordsExistError
): string {
  if (error.childType === 'consumedTool') {
    return `This platform has ${error.count} tool(s). Delete them in Phase 2 first.`;
  }

  if (error.childType === 'consumedToolDeployment') {
    return `This tool has ${error.count} deployment(s). Delete them in Phase 3 first.`;
  }

  return `Cannot delete: ${error.count} related record(s) must be removed first.`;
}

function parseJsonObject(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function extractErrorBody(error: unknown): unknown {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  const candidate = error as {
    body?: unknown;
    content?: unknown;
    message?: unknown;
    status?: unknown;
  };

  if (isLtiChildRecordsExistError(candidate.body)) {
    return candidate.body;
  }

  if (isLtiChildRecordsExistError(candidate.content)) {
    return candidate.content;
  }

  if (typeof candidate.message === 'string') {
    const parsed = parseJsonObject(candidate.message);
    if (isLtiChildRecordsExistError(parsed)) {
      return parsed;
    }
  }

  return null;
}

export function alertLtiDeleteError(error: unknown): void {
  const body = extractErrorBody(error);
  if (isLtiChildRecordsExistError(body)) {
    window.alert(formatLtiChildRecordsExistMessage(body));
    return;
  }

  const message =
    error instanceof Error && error.message
      ? error.message
      : 'Delete failed. Please try again.';
  window.alert(message);
}

export function buildDeploymentDeleteConfirmMessage(
  deploymentId: string,
  resourceLinks: Array<{
    platformResourceLinkId: string;
    toolItemType: string;
    toolItemId: string;
  }>
): string {
  if (resourceLinks.length === 0) {
    return `Are you sure you want to delete deployment ${deploymentId}?`;
  }

  const linkLines = resourceLinks
    .map(
      (link) =>
        `- ${link.platformResourceLinkId} → ${link.toolItemType} ${link.toolItemId}`
    )
    .join('\n');

  return [
    `Delete deployment ${deploymentId}?`,
    '',
    'This will also remove the following cohort LTI mappings:',
    linkLines,
    '',
    'LTI enrolment for those cohorts will stop working.',
  ].join('\n');
}
